import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { WindowToggle } from "@/components/window-toggle";
import { Table, Th, Td, fmtUsd } from "@/components/data-table";
import { ChainBadge } from "@/components/chain-badge";
import { RarityPill } from "@/components/rarity-pill";
import { ScrambleText } from "@/components/scramble-text";
import { CardImage } from "@/components/card-image";
import { makeWatchlistKey, useWatchlist } from "@/hooks/use-watchlist";
import type { WindowLabel } from "@/lib/types";
import type { RarityName } from "@/lib/abis";

interface Row {
  chain: "base" | "apechain";
  slabAddress: string;
  tokenId: string;
  pullCount: number;
  latestSlabValueUsd: number;
  rarity: RarityName;
  pack: string;
  externalTokenId?: string;
  imageUrl?: string;
  description?: string;
  courtyardSales7d?: number;
  courtyardMedianUsd?: number;
  courtyardLastSaleUsd?: number;
}

export default function SinglesPage() {
  const [window, setWindow] = useState<WindowLabel>("7d");
  const { add, remove, isWatched } = useWatchlist();
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["singles", window],
    queryFn: async () =>
      (await (await fetch(`/api/trending-singles?window=${window}`)).json()) as {
        rows: Row[];
      },
  });

  function toggleWatch(r: Row) {
    const key = makeWatchlistKey({
      kind: "CARD",
      chain: r.chain,
      address: r.slabAddress,
      tokenId: r.tokenId,
    });
    if (isWatched(key)) {
      remove(key);
      return;
    }
    add({
      kind: "CARD",
      chain: r.chain,
      chainId: r.chain === "base" ? 8453 : 33139,
      address: r.slabAddress,
      tokenId: r.tokenId,
      description: r.description,
      imageUrl: r.imageUrl,
      rarity: r.rarity,
      valueUsd: r.latestSlabValueUsd,
    });
  }

  return (
    <div className="p-3 md:p-4 space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold uppercase tracking-[0.2em] neon-text cursor">
            <ScrambleText text="trending singles" duration={900} revealEach={45} />
          </h1>
          <p className="text-[11px] text-muted max-w-3xl font-mono">
            <span className="text-accent2">›</span> cards ranked by pulls across base + apechain ·
            underlying graded slabs live on solana via collector crypt
          </p>
        </div>
        <div className="flex items-center gap-2">
          <WindowToggle value={window} onChange={setWindow} />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded border border-border bg-panel/60 px-3 py-1 text-xs font-mono uppercase tracking-wider hover:border-accent hover:text-accent hover:shadow-glow transition-all disabled:opacity-50"
          >
            {isFetching ? "› syncing…" : "[refresh]"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded border border-border bg-panel/60 p-6 text-sm text-muted font-mono">
          <span className="text-accent2">›</span> scanning pulls and courtyard sales
          <span className="animate-blink text-accent ml-1">_</span>
        </div>
      ) : (
        <>
        {/* Mobile: 2-col card grid matching top-pulls vibes. */}
        <div className="md:hidden grid grid-cols-2 gap-2">
          {(data?.rows ?? []).map((r, i) => {
            const key = makeWatchlistKey({
              kind: "CARD",
              chain: r.chain,
              address: r.slabAddress,
              tokenId: r.tokenId,
            });
            const watched = isWatched(key);
            return (
              <div
                key={key}
                className="relative flex flex-col overflow-hidden rounded border border-border bg-panel/80 hover:border-accent transition-colors animate-fadeIn"
              >
                <div className="absolute top-1 left-1 z-10 rounded bg-bg/80 px-1 py-0.5 text-[9px] font-mono text-muted">
                  #{i + 1}
                </div>
                <div className="absolute top-1 right-1 z-10">
                  <ChainBadge chain={r.chain} />
                </div>
                <a
                  href={`https://opensea.io/assets/${
                    r.chain === "base" ? "base" : "ape_chain"
                  }/${r.slabAddress}/${r.tokenId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col"
                >
                  <div className="relative bg-bg aspect-[3/4] overflow-hidden">
                    {r.imageUrl ? (
                      <CardImage
                        src={r.imageUrl}
                        alt={r.description ?? `Slab #${r.tokenId}`}
                        size={220}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-muted font-mono">
                        ?
                      </div>
                    )}
                  </div>
                </a>
                <div className="p-2 space-y-1 text-[10px] font-mono">
                  <div className="truncate text-text" title={r.description}>
                    {r.description ?? `Slab #${r.tokenId}`}
                  </div>
                  <div className="flex items-center justify-between">
                    <RarityPill rarity={r.rarity} />
                    <span className="font-bold text-accent neon-text">
                      {fmtUsd(r.latestSlabValueUsd)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-1 pt-1 border-t border-border/60">
                    <span className="text-muted">
                      pulls <span className="text-accent2">{r.pullCount}</span>
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleWatch(r);
                      }}
                      className={
                        "rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider transition-all " +
                        (watched
                          ? "border-gold/60 text-gold bg-gold/10 shadow-[0_0_8px_rgba(255,176,32,0.4)]"
                          : "border-border text-muted")
                      }
                    >
                      {watched ? "★" : "☆"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th />
              <Th>Card</Th>
              <Th>Chain</Th>
              <Th>Rarity</Th>
              <Th className="text-right">Pulls</Th>
              <Th className="text-right">Slab Value</Th>
              <Th className="text-right">Courtyard Sales</Th>
              <Th className="text-right">Courtyard Median</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((r, i) => {
              const key = makeWatchlistKey({
                kind: "CARD",
                chain: r.chain,
                address: r.slabAddress,
                tokenId: r.tokenId,
              });
              const watched = isWatched(key);
              return (
                <tr key={key} className="hover:bg-border/20 row-glow">
                  <Td className="text-muted">{i + 1}</Td>
                  <Td className="w-[88px]">
                    <div className="h-[112px] w-[80px] overflow-hidden rounded border border-border bg-bg">
                      {r.imageUrl ? (
                        <CardImage
                          src={r.imageUrl}
                          alt={r.description ?? `Slab #${r.tokenId}`}
                          size={240}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[10px] text-muted font-mono">
                          ?
                        </div>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <div className="font-medium truncate max-w-[220px]" title={r.description}>
                      {r.description ?? `Slab #${r.tokenId}`}
                    </div>
                    <div className="text-[10px] text-muted font-mono">#{r.tokenId}</div>
                    {/* Link to the slab NFT on OpenSea. OpenSea now supports
                        ApeChain under the `ape_chain` slug, so we use the
                        same marketplace for both networks. */}
                    <a
                      className="text-[10px] font-mono text-muted hover:text-accent"
                      target="_blank"
                      rel="noreferrer"
                      href={`https://opensea.io/assets/${
                        r.chain === "base" ? "base" : "ape_chain"
                      }/${r.slabAddress}/${r.tokenId}`}
                      title={`View slab #${r.tokenId} on OpenSea`}
                    >
                      opensea ↗
                    </a>
                  </Td>
                  <Td><ChainBadge chain={r.chain} /></Td>
                  <Td><RarityPill rarity={r.rarity} /></Td>
                  <Td className="text-right">{r.pullCount}</Td>
                  <Td className="text-right">{fmtUsd(r.latestSlabValueUsd)}</Td>
                  <Td className="text-right">{r.courtyardSales7d ?? 0}</Td>
                  <Td className="text-right">{fmtUsd(r.courtyardMedianUsd)}</Td>
                  <Td>
                    <button
                      onClick={() => toggleWatch(r)}
                      className={
                        "rounded border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider transition-all " +
                        (watched
                          ? "border-gold/60 text-gold bg-gold/10 hover:bg-gold/20 shadow-[0_0_8px_rgba(255,176,32,0.4)]"
                          : "border-border hover:border-accent hover:text-accent hover:shadow-glow")
                      }
                      title={watched ? "Remove from watchlist" : "Add to watchlist"}
                    >
                      {watched ? "[watching ★]" : "[watch ☆]"}
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </>
      )}
    </div>
  );
}
